'use client';

import type { ReactNode } from 'react';

import { MOBILE_SAFE_AREA_TOP } from '@/components/work-items/mobileSafeArea';
import { cn } from '@/lib/utils';

import { CAPACITOR_CONNECT_PANEL_BOTTOM } from './constants';

export interface TotalConnectRtcIdleShellProps {
  onBackdropClose: () => void;
  isMobileApp: boolean;
  backdropZIndexClass: string;
  panelZIndexClass: string;
  isDark: boolean;
  /**
   * slideover: floating panel + backdrop (portal / header modal).
   * embedded: fills parent — for standalone desktop suite (no slide-over wrapper).
   */
  variant?: 'slideover' | 'embedded';
  /** Match Email/Messages modals: spacer only, no duplicate header safe-area padding */
  showMobileTopSpacer?: boolean;
  header: ReactNode;
  scrollContent: ReactNode;
  /**
   * Embedded-desktop only: second column beside scrollContent (e.g. branded idle “stage”).
   * Ignored for slideover and mobile layouts.
   */
  idleSplitAside?: ReactNode | null;
  idleTabBar: ReactNode | null;
  nonIdleBottomSpacer: ReactNode | null;
  overlays: ReactNode;
}

/**
 * Sliding panel chrome for Total Connect idle UX (contacts / recents / keypad).
 * Content, tab bar, and overlays are composed by the parent to avoid prop explosion.
 */
export function TotalConnectRtcIdleShell({
  onBackdropClose,
  isMobileApp,
  backdropZIndexClass,
  panelZIndexClass,
  isDark,
  variant = 'slideover',
  showMobileTopSpacer = false,
  header,
  scrollContent,
  idleSplitAside = null,
  idleTabBar,
  nonIdleBottomSpacer,
  overlays,
}: TotalConnectRtcIdleShellProps) {
  const embedded = variant === 'embedded' && !isMobileApp;
  // Single-pane slideover: the split stage is reserved for the embedded desktop suite.
  const useIdleSplit = embedded && idleSplitAside != null;

  const innerChrome = (
    <div
      className={`${isDark ? 'bg-gray-900' : 'bg-white'} flex h-full min-h-0 flex-col`}
    >
      {showMobileTopSpacer ? (
        <div
          aria-hidden="true"
          className={`flex-shrink-0 ${isDark ? 'bg-gray-900' : 'bg-white'}`}
          style={{ height: MOBILE_SAFE_AREA_TOP }}
        />
      ) : null}
      {header}
      {useIdleSplit ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto lg:max-w-lg lg:flex-shrink-0 xl:max-w-xl">
            {scrollContent}
          </div>
          <div
            className={cn(
              'hidden min-h-0 min-w-0 flex-1 flex-col lg:flex lg:overflow-hidden',
              'border-t lg:border-l lg:border-t-0',
              isDark ? 'border-gray-800/80 bg-gray-900/40' : 'border-gray-200/70 bg-gray-50/80',
            )}
          >
            {idleSplitAside}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">{scrollContent}</div>
      )}
      {idleTabBar}
      {nonIdleBottomSpacer}
      {overlays}
    </div>
  );

  if (embedded) {
    return (
      <div
        className={cn(
          'flex h-full min-h-0 w-full flex-col overflow-hidden',
          isDark ? 'bg-gray-900' : 'bg-white',
        )}
      >
        {innerChrome}
      </div>
    );
  }

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/30 backdrop-blur-sm transition-all duration-300 ${backdropZIndexClass}`}
        onClick={onBackdropClose}
      />

      <div
        className={cn(
          `fixed ${panelZIndexClass} animate-slide-in-right right-0 top-0 w-full max-w-2xl shadow-2xl flex flex-col`,
          isDark ? 'bg-gray-900' : 'bg-white',
        )}
        style={{ bottom: CAPACITOR_CONNECT_PANEL_BOTTOM }}
      >
        {innerChrome}
      </div>

      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
